import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Clock, Award, Heart, Leaf } from "lucide-react";
import { useSite } from "@/lib/api";
import Seo from "@/components/common/Seo";
import { EDITORIAL } from "@/lib/site";
import SectionHeading from "@/components/common/SectionHeading";

export default function About() {
  const { data: site } = useSite();

  return (
    <div data-testid="about-page" className="bg-cream">
      <Seo title="Hakkımızda" description="1985'ten beri butik kasaplık. Ustalık, güven ve doğru et seçimi. Ertan Kasap'ın hikâyesi." />
      <section className="border-b border-dashed border-bordo/30 pt-16 pb-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Est. 1985" title="Ustanın Elinden, Üç Nesildir" description={site?.sub_slogan} />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <img src={EDITORIAL.butcher} alt="Usta kasap" className="aspect-[4/3] w-full border-4 border-charcoal object-cover" />
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-bordo">Hikayemiz</span>
            <h2 className="mt-3 font-serif text-4xl text-charcoal">1985’ten Bugüne Aynı Tutku</h2>
            <p className="mt-4 leading-relaxed text-[#6b645c]">
              Ertan Kasap, 1985 yılında küçük bir mahalle dükkânında, kaliteli eti dürüstçe sunma idealiyle kuruldu. Bugün de aynı titizlikle çalışıyoruz: doğru kaynaktan gelen, usta eliyle hazırlanan ve günlük tazelikte sunulan et.
            </p>
            <p className="mt-4 leading-relaxed text-[#6b645c]">
              Bizim için kasaplık bir zanaat. Hangi etin nereye gittiğini, hangi kesimin nasıl pişeceğini bilir; doğru ürünü doğru sofraya buluştururuz. “İyi et tesadüf değildir” sözümüz, işte bu birikimin ifadesidir.
            </p>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-1 gap-px border border-dashed border-bordo/20 bg-bordo/10 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: ShieldCheck, t: "Güven", d: "Şeffaf, dürüst ve standartlara tam uyumlu kasaplık." },
            { icon: Clock, t: "Tazelik", d: "Her ürün günlük hazırlanır; bayatlık asla raf görmez." },
            { icon: Award, t: "Uzmanlık", d: "40 yılı aşkın tecrübeyle doğru kesim, doğru öneri." },
            { icon: Heart, t: "Samimiyet", d: "Mahalle kasabı sıcaklığı, butik et uzmanlığıyla buluşuyor." },
          ].map((v) => (
            <div key={v.t} className="bg-paper p-7">
              <span className="flex h-11 w-11 items-center justify-center border border-dashed border-bordo/40">
                <v.icon className="h-5 w-5 text-bordo" />
              </span>
              <h4 className="mt-4 font-serif text-xl text-charcoal">{v.t}</h4>
              <p className="mt-1.5 text-sm leading-relaxed text-[#8C857D]">{v.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-20 grid items-center gap-12 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-bordo">Zanaat</span>
            <h2 className="mt-3 font-serif text-4xl text-charcoal">Zanaatın İncelikleri</h2>
            <p className="mt-4 leading-relaxed text-[#6b645c]">
              Dry aged dinlendirmeden, fermente sucuğa; çemenli pastırmadan, ayarında köfteye kadar her ürünümüz geleneksel yöntemlerle, sabırla hazırlanır. Lezzet aceleye gelmez.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 font-mono text-sm text-bordo">
              <Leaf className="h-4 w-4" /> Katkısız, gerçek kasap usulü.
            </div>
            <div>
              <Link to="/urunler" className="mt-8 inline-flex items-center gap-2 rounded-sm bg-bordo px-6 py-3 text-sm font-bold uppercase tracking-wide text-cream hover:bg-bordo-dark transition-colors">
                Ürünlerimizi Keşfedin
              </Link>
            </div>
          </div>
          <img src={EDITORIAL.charcuterie} alt="Şarküteri" className="order-1 aspect-[4/3] w-full border-4 border-charcoal object-cover lg:order-2" />
        </div>
      </section>
    </div>
  );
}
